import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, X } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";
import { t } from "@/utils/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fullscreen overlay that plays when the player obtains a "special" card
 * (foil, epic, or legendary). Shows a Pokemon-booster-style reveal animation.
 *
 * Usage: <CardRevealOverlay cards={[card1, card2]} onClose={() => setQueue([])} />
 */
export default function CardRevealOverlay({ cards = [], onClose, lang = "en" }) {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("pack"); // pack | reveal

  useEffect(() => {
    if (cards.length === 0) return;
    setIndex(0);
    setPhase("pack");
    const t1 = setTimeout(() => setPhase("reveal"), 900);
    return () => clearTimeout(t1);
  }, [cards]);

  if (!cards || cards.length === 0) return null;
  const card = cards[index];
  if (!card || !card.id) {
    // Skip malformed card; auto-close to avoid stuck overlay
    return null;
  }

  const rarity = (card.rarity || "common").toLowerCase();
  const isFoil = !!card.foil;
  const rarityColor =
    rarity === "legendary" ? "#f59e0b"
    : rarity === "epic" ? "#a855f7"
    : rarity === "rare" ? "#3b82f6"
    : rarity === "uncommon" ? "#22c55e" : "#94a3b8";

  const next = () => {
    if (index + 1 < cards.length) {
      setIndex(i => i + 1);
      setPhase("pack");
      setTimeout(() => setPhase("reveal"), 900);
    } else {
      onClose?.();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-fadeInUp"
      onClick={next}
      data-testid="card-reveal-overlay"
    >
      {/* Close X button — always visible, prominent on mobile */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-[#1a1a1d] border-2 border-[#cfb53b]/50 text-[#cfb53b] hover:bg-[#cfb53b]/10 flex items-center justify-center transition-colors"
        data-testid="card-reveal-close"
        aria-label="Close"
      >
        <X size={22} />
      </button>

      {/* Dramatic radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${rarityColor}33 0%, transparent 60%)`
        }}
      />

      <div className="relative flex flex-col items-center gap-4">
        {/* Header */}
        <div className="text-center">
          <p className="text-[10px] font-heading uppercase tracking-[0.3em] text-[#cfb53b] mb-1">
            {rarity !== "common" ? t(lang, `rarity_${rarity}`) : t(lang, "new_item")}
            {isFoil && <span className="ml-2 text-[#fef08a]">✨ FOIL</span>}
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#e2e8f0] drop-shadow-lg">
            {phase === "pack" ? (lang.startsWith("it") ? "Oggetto raro!" : "Rare Drop!") : card.name}
          </h2>
        </div>

        {/* Card reveal */}
        <div className={`reveal-card ${phase === "reveal" ? "reveal-phase" : "pack-phase"} rarity-${rarity}`}>
          <div className="reveal-card-inner w-full h-full">
            {phase === "pack" ? (
              <div className="mtg-card card-basic w-full h-full pointer-events-none">
                <div className="mtg-inner-frame w-full h-full flex items-center justify-center bg-radial from-[#1e1a15] to-[#0a0a0c]">
                  {/* Glowing gold seal in the center representing card back */}
                  <div className="w-20 h-20 rounded-full border-2 border-[#cfb53b]/60 flex items-center justify-center bg-black/60 shadow-[0_0_20px_rgba(207,181,59,0.3)] animate-pulse">
                    <Sparkles size={36} className="text-[#cfb53b]" />
                  </div>
                </div>
              </div>
            ) : (
              <div className={`mtg-card card-${card.type || "ability"} rarity-${rarity} w-full h-full`}>
                <div className={`mtg-inner-frame ${isFoil ? "card-foil" : ""}`}>
                  {/* Header Bar */}
                  <div className="mtg-header-bar">
                    <span className="mtg-card-title">{card.name}</span>
                    <div className="mtg-header-right">
                      {card.mana_cost > 0 && (
                        <div className="mtg-mana-gem">
                          <span>{card.mana_cost}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Artwork Frame */}
                  <div className="mtg-art-frame">
                    {card.image_url ? (
                      <img
                        src={resolveImageUrl(card.image_url, BACKEND_URL)}
                        alt={card.name}
                        className="mtg-art-image"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <div className="mtg-no-art">
                        <Sparkles size={32} style={{ color: rarityColor }} />
                      </div>
                    )}
                  </div>

                  {/* Type Line */}
                  <div className="mtg-type-line">
                    <span className="mtg-type-text">
                      {t(lang, `type_${card.type || "ability"}`)}
                    </span>
                    <div className="mtg-type-right">
                      {rarity !== "common" && (
                        <span className="mtg-rarity-indicator font-heading" style={{ color: rarityColor }}>
                          ✦
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Rules / Text Box */}
                  <div className="mtg-rules-box">
                    <div className="mtg-description">
                      {card.effect && card.effect !== "none" && (card.effect_value || 0) > 0 && (
                        <div className="mb-0.5 font-heading text-[8px] uppercase tracking-wider">
                          {card.effect === "damage" && <span className="text-red-600">{lang.startsWith("it") ? "DANNO" : "DMG"}: {card.effect_value}</span>}
                          {card.effect === "heal" && <span className="text-green-600">{lang.startsWith("it") ? "CURA" : "HEAL"}: {card.effect_value}</span>}
                          {card.effect === "buff" && <span className="text-amber-600">BUFF: +{card.effect_value}</span>}
                          {card.effect === "debuff" && <span className="text-purple-600">DEBUFF: -{card.effect_value}</span>}
                        </div>
                      )}
                      <p className="line-clamp-3">{card.description}</p>
                    </div>

                    {/* Stat Plaque for combat stats */}
                    {(card.type === "ally" || (card.type === "object" && ((card.attack || 0) > 0 || (card.defense || 0) > 0))) && (
                      <div className="mtg-stat-plaque">
                        {card.type === "ally" ? (
                          <>
                            <span>{card.attack ?? 0}</span>/<span>{card.max_hp ?? card.hp ?? 0}</span>
                          </>
                        ) : (
                          <>
                            {card.attack > 0 && <span>+{card.attack}</span>}
                            {card.attack > 0 && card.defense > 0 && <span>/</span>}
                            {card.defense > 0 && <span>+{card.defense}</span>}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-[#cfb53b]/15 border border-[#cfb53b]/40 text-[#cfb53b] font-heading text-xs px-4 py-2 rounded-full animate-pulse" data-testid="card-reveal-tap-hint">
          {cards.length > 1
            ? `${index + 1} / ${cards.length} — ${lang.startsWith("it") ? "TOCCA PER CONTINUARE" : "TAP TO CONTINUE"}`
            : (lang.startsWith("it") ? "TOCCA PER CHIUDERE" : "TAP TO CLOSE")}
        </div>
      </div>
    </div>,
    document.body
  );
}
